import { createSlice } from "@reduxjs/toolkit";
import { apiCallerExt } from "../../api/apiCallerExt";
import { promotionEngineApi } from "../../sharedfiles/EndpointConfig";
import { toaster } from "../../sharedfiles/helper";

/**
 * State for the Promotions Engine hub (/promotions-engine): campaigns,
 * lotteries, badges and loyalty tier configs, all served by the standalone
 * promotion-service. List payloads come from SQL functions on the backend and
 * are untyped JSON, so every thunk parses defensively and the tabs render
 * whatever columns are present.
 */
export const PromotionsEngineSlice = createSlice({
  name: "promotionsEngine",
  initialState: {
    // campaigns
    campaigns: [],
    campaignsTotal: 0,
    campaignsLoading: false,
    participants: [],
    participantsLoading: false,
    participantsFor: null,
    // lotteries
    lotteries: [],
    lotteriesLoading: false,
    lotteryWinners: [],
    lotteryWinnersFor: null,
    // badges
    badges: [],
    badgesLoading: false,
    playerBadges: [],
    // tier config
    tierConfigs: [],
    tierConfigsLoading: false,
    // shared
    submitting: false,
  },
  reducers: {
    setCampaigns: (state, action) => {
      state.campaigns = action.payload.rows;
      state.campaignsTotal = action.payload.total;
    },
    setCampaignsLoading: (state, action) => {
      state.campaignsLoading = action.payload;
    },
    setParticipants: (state, action) => {
      state.participants = action.payload.rows;
      state.participantsFor = action.payload.promotionId;
    },
    setParticipantsLoading: (state, action) => {
      state.participantsLoading = action.payload;
    },
    setLotteries: (state, action) => {
      state.lotteries = action.payload;
    },
    setLotteriesLoading: (state, action) => {
      state.lotteriesLoading = action.payload;
    },
    setLotteryWinners: (state, action) => {
      state.lotteryWinners = action.payload.rows;
      state.lotteryWinnersFor = action.payload.lotteryId;
    },
    setBadges: (state, action) => {
      state.badges = action.payload;
    },
    setBadgesLoading: (state, action) => {
      state.badgesLoading = action.payload;
    },
    setPlayerBadges: (state, action) => {
      state.playerBadges = action.payload;
    },
    setTierConfigs: (state, action) => {
      state.tierConfigs = action.payload;
    },
    setTierConfigsLoading: (state, action) => {
      state.tierConfigsLoading = action.payload;
    },
    setSubmitting: (state, action) => {
      state.submitting = action.payload;
    },
  },
});

export const {
  setCampaigns,
  setCampaignsLoading,
  setParticipants,
  setParticipantsLoading,
  setLotteries,
  setLotteriesLoading,
  setLotteryWinners,
  setBadges,
  setBadgesLoading,
  setPlayerBadges,
  setTierConfigs,
  setTierConfigsLoading,
  setSubmitting,
} = PromotionsEngineSlice.actions;

const asArray = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.content)) return res.content;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.content)) return res.data.content;
  return [];
};

const totalOf = (res, rows) =>
  res?.totalElements ?? res?.totalRecords ?? res?.data?.totalElements ?? rows.length;

const errText = (error, fallback) =>
  (typeof error?.response === "string" && error.response) || fallback;

// ─── Campaigns ───────────────────────────────────────────────────────────────

export const fetchCampaigns = (filters) => (dispatch) => {
  dispatch(setCampaignsLoading(true));
  return apiCallerExt(promotionEngineApi.listCampaigns(filters), "GET")
    .then((res) => {
      const rows = asArray(res);
      dispatch(setCampaigns({ rows, total: totalOf(res, rows) }));
    })
    .catch((error) => {
      dispatch(setCampaigns({ rows: [], total: 0 }));
      toaster.error(errText(error, "Failed to load campaigns"));
    })
    .finally(() => dispatch(setCampaignsLoading(false)));
};

export const updateCampaignStatus = (id, action, refreshFilters) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(promotionEngineApi.updateCampaignStatus(id, action), "PUT", {})
    .then(() => {
      toaster.success(`Campaign marked ${action}`);
      if (refreshFilters) dispatch(fetchCampaigns(refreshFilters));
    })
    .catch((error) => toaster.error(errText(error, "Status update failed")))
    .finally(() => dispatch(setSubmitting(false)));
};

export const fetchParticipants = (promotionId) => (dispatch) => {
  dispatch(setParticipantsLoading(true));
  return apiCallerExt(promotionEngineApi.campaignParticipants(promotionId), "GET")
    .then((res) => dispatch(setParticipants({ rows: asArray(res), promotionId })))
    .catch((error) => {
      dispatch(setParticipants({ rows: [], promotionId }));
      toaster.error(errText(error, "Failed to load participants"));
    })
    .finally(() => dispatch(setParticipantsLoading(false)));
};

export const reloadPromotionCache = () => () =>
  apiCallerExt(promotionEngineApi.reloadCache, "GET")
    .then(() => toaster.success("Promotion cache reloaded"))
    .catch((error) => toaster.error(errText(error, "Cache reload failed")));

// Resolves the { success, message } outcome so the form can decide navigation.
export const createCampaign = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(promotionEngineApi.createCampaign, "POST", payload)
    .then((res) => {
      const ok = res?.status === 200 || res?.status === "success" || res?.mission_id;
      if (ok) toaster.success("Campaign created");
      else toaster.error(res?.message || "Campaign creation failed");
      return { success: !!ok, message: res?.message };
    })
    .catch((error) => {
      const message = errText(error, "Campaign creation failed");
      toaster.error(message);
      return { success: false, message };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

// ─── Lotteries ───────────────────────────────────────────────────────────────

export const fetchLotteries = (status, isRegular) => (dispatch) => {
  dispatch(setLotteriesLoading(true));
  return apiCallerExt(promotionEngineApi.lotteryList(status, isRegular), "GET")
    .then((res) => dispatch(setLotteries(asArray(res))))
    .catch((error) => {
      dispatch(setLotteries([]));
      toaster.error(errText(error, "Failed to load lotteries"));
    })
    .finally(() => dispatch(setLotteriesLoading(false)));
};

export const createLottery = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(promotionEngineApi.createLottery, "POST", payload)
    .then(() => {
      toaster.success("Lottery created");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Lottery creation failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const updateLottery = (id, payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(promotionEngineApi.lotteryConfig(id), "PUT", payload)
    .then(() => {
      toaster.success("Lottery updated");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Lottery update failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const fetchLotteryWinners = (lotteryId) => (dispatch) =>
  apiCallerExt(promotionEngineApi.lotteryWinners(lotteryId), "GET")
    .then((res) => {
      const data = res?.winners || res?.data?.winners || asArray(res);
      dispatch(setLotteryWinners({ rows: Array.isArray(data) ? data : [], lotteryId }));
    })
    .catch((error) => {
      dispatch(setLotteryWinners({ rows: [], lotteryId }));
      toaster.error(errText(error, "Failed to load winners"));
    });

export const grantLotteryTickets = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(promotionEngineApi.grantTickets, "POST", payload)
    .then(() => {
      toaster.success("Tickets granted");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Ticket grant failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const rescheduleLotteryDraws = () => () =>
  apiCallerExt(promotionEngineApi.rescheduleDraws, "GET")
    .then(() => toaster.success("Pending draws rescheduled"))
    .catch((error) => toaster.error(errText(error, "Reschedule failed")));

// ─── Badges ──────────────────────────────────────────────────────────────────

export const fetchBadges = () => (dispatch) => {
  dispatch(setBadgesLoading(true));
  return apiCallerExt(promotionEngineApi.availableBadges, "GET")
    .then((res) => dispatch(setBadges(asArray(res))))
    .catch((error) => {
      dispatch(setBadges([]));
      toaster.error(errText(error, "Failed to load badges"));
    })
    .finally(() => dispatch(setBadgesLoading(false)));
};

export const createBadge = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(promotionEngineApi.createBadge, "POST", payload)
    .then(() => {
      toaster.success("Badge created");
      dispatch(fetchBadges());
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Badge creation failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const toggleBadgeRule = (badgeName, ruleName, isActive) => (dispatch) =>
  apiCallerExt(promotionEngineApi.badgeRuleStatus(badgeName, ruleName, isActive), "PUT", {})
    .then(() => {
      toaster.success(`Rule ${isActive ? "enabled" : "disabled"}`);
      dispatch(fetchBadges());
    })
    .catch((error) => toaster.error(errText(error, "Rule update failed")));

export const deleteBadgeRule = (badgeName, ruleName) => (dispatch) =>
  apiCallerExt(promotionEngineApi.deleteBadgeRule(badgeName, ruleName), "DELETE", {})
    .then(() => {
      toaster.success("Rule deleted");
      dispatch(fetchBadges());
    })
    .catch((error) => toaster.error(errText(error, "Rule delete failed")));

export const fetchPlayerBadges = (userId, brandId) => (dispatch) =>
  apiCallerExt(promotionEngineApi.playerBadges(userId, brandId), "GET")
    .then((res) => dispatch(setPlayerBadges(asArray(res))))
    .catch((error) => {
      dispatch(setPlayerBadges([]));
      toaster.error(errText(error, "Failed to load player badges"));
    });

// ─── Loyalty tier config ─────────────────────────────────────────────────────

export const fetchTierConfigs = (operatorId, brandId) => (dispatch) => {
  dispatch(setTierConfigsLoading(true));
  return apiCallerExt(promotionEngineApi.tierConfigList(operatorId, brandId), "GET")
    .then((res) => dispatch(setTierConfigs(asArray(res))))
    .catch((error) => {
      dispatch(setTierConfigs([]));
      toaster.error(errText(error, "Failed to load tier configs"));
    })
    .finally(() => dispatch(setTierConfigsLoading(false)));
};

export const saveTierConfig = (payload, existing) => (dispatch) => {
  dispatch(setSubmitting(true));
  const url = existing
    ? promotionEngineApi.updateTierConfig(payload.tierId, payload.operatorId, payload.brandId)
    : promotionEngineApi.tierConfig;
  return apiCallerExt(url, existing ? "PUT" : "POST", payload)
    .then(() => {
      toaster.success(existing ? "Tier config updated" : "Tier config created");
      dispatch(fetchTierConfigs(payload.operatorId, payload.brandId));
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Tier config save failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export default PromotionsEngineSlice.reducer;
